import { useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, Pencil } from 'lucide-react';
import type { Item } from '@milk/shared';
import { getState, toggleItem, updateItem, useStore } from '../store/store';
import { useToast } from './ui';
import { SectionPicker } from './SectionPicker';
import { ItemEditor } from './ItemEditor';

/** Checking an item moves it (to or from the cart): keep keyboard focus on it when it lands. */
let refocus: string | null = null;

export function ItemRow({ item, storeId, flash }: { item: Item; storeId: string | null; flash?: boolean }) {
  const [moving, setMoving] = useState(false);
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const stores = useStore((s) => s.stores);
  const id = `item-${item.id}`;
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (refocus === item.id) { refocus = null; box.current?.focus(); }
  }, [item.checked, item.id]);
  // which stores it's for, when that's not obvious from the store being shown
  const tags = item.storeIds.length && !(storeId && item.storeIds.length === 1 && item.storeIds[0] === storeId)
    ? item.storeIds.map((s) => stores[s]?.name).filter(Boolean) as string[]
    : [];

  return (
    <li className={`item${item.checked ? ' done' : ''}${flash ? ' flash' : ''}`}>
      <label className="check" htmlFor={id}>
        <input id={id} ref={box} type="checkbox" checked={item.checked} onChange={(e) => { if (document.activeElement === e.target) refocus = item.id; toggleItem(item.id); }} aria-describedby={`${id}-meta`} />
        <span className="text">
          <span className="name">{item.name}</span>
          <span className="meta" id={`${id}-meta`}>
            {item.quantity && <span className="qty">{item.quantity}</span>}
            {item.note && <span>{item.note}</span>}
            {tags.length > 0 && <span className="store-tag">Only at {tags.join(', ')}</span>}
          </span>
        </span>
      </label>
      {!item.checked && (
        <button className="icon-btn" onClick={() => setMoving(true)} aria-label={`Move ${item.name} to another section`} title="Move to another section">
          <ArrowRightLeft size={19} aria-hidden />
        </button>
      )}
      <button className="icon-btn" onClick={() => setEditing(true)} aria-label={`Edit ${item.name}`} title="Edit">
        <Pencil size={18} aria-hidden />
      </button>
      {moving && (
        <SectionPicker
          title={`Move ${item.name} to…`}
          note="It will go there from now on, for everyone."
          current={item.categoryId}
          storeId={storeId}
          onClose={() => setMoving(false)}
          onPick={(cid) => {
            setMoving(false);
            if (cid === item.categoryId) return;
            const before = item.categoryId;
            updateItem(item.id, { categoryId: cid });
            const name = getState().categories[cid]?.name ?? 'there';
            toast(`Moved ${item.name} to ${name}. Remembered for next time.`, 'info', { label: 'Undo', run: () => updateItem(item.id, { categoryId: before }) });
          }}
        />
      )}
      {editing && <ItemEditor item={item} storeId={storeId} onClose={() => setEditing(false)} />}
    </li>
  );
}
